import React, { useState, useEffect, useMemo, useRef } from 'react';
import { spanish } from '../../api';
import { playSpanish, stopSpanishAudio } from '../../utils/spanishAudio';
import './Spanish.css';

const prefersReducedMotion = () => !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

const GRADING_MESSAGES = ['Collecting your answers…', 'Checking each word…', 'Counting your points…', 'Almost there…'];
const CONFETTI_COLORS = ['#f2a900', '#c8102e', '#2f4ba2', '#1f9d55', '#ffffff', '#ff7d8d'];
const RING_R = 54;
const RING_LEN = 2 * Math.PI * RING_R;

/** Counts from 0 up to `target` over `ms` (jumps straight there when motion is reduced). */
function useCountUp(target, ms, start) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!start) { setN(0); return undefined; }
    if (prefersReducedMotion()) { setN(target); return undefined; }
    let raf; const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms, start]);
  return n;
}

/** Falling confetti, drawn above everything (CSS only; stops by itself). */
function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 46 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.7,
    dur: 2.4 + Math.random() * 1.6,
    drift: (Math.random() - 0.5) * 160,
    spin: 360 + Math.random() * 540,
    size: 6 + Math.random() * 6,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    round: i % 3 === 0
  })), []);
  return (
    <div className="sp-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <i key={i} style={{
          left: `${p.left}%`, width: p.size, height: p.round ? p.size : p.size * 1.8, background: p.color,
          borderRadius: p.round ? '50%' : '2px', animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`,
          '--drift': `${p.drift}px`, '--spin': `${p.spin}deg`
        }} />
      ))}
    </div>
  );
}

/**
 * A level exam: questions come only from words the student has studied in that level.
 * No right/wrong feedback while answering - the whole exam is graded at the end, on the server.
 * Flow: questions -> grading scene -> animated result (confetti on a pass) -> onClose(result).
 * Passing opens the next level (the Spanish tab then scrolls to it); missed words go back to practice.
 *
 * props: level, onClose(result | null)
 */
export default function SpanishExam({ level, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exam, setExam] = useState(null);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});   // question index -> option id
  const [grading, setGrading] = useState(false);
  const [msgIndex, setMsgIndex] = useState(0);
  const [result, setResult] = useState(null);
  const [shown, setShown] = useState(false);    // flips true a moment after the result arrives, to run the transitions
  const [settled, setSettled] = useState(false);  // safety net: after a few seconds everything is simply shown, animated or not
  const gradingStarted = useRef(0);

  const gradingMs = prefersReducedMotion() ? 500 : 2800;

  useEffect(() => {
    let cancelled = false;
    spanish.startExam(level)
      .then(res => { if (!cancelled) setExam(res.data); })
      .catch(e => { if (!cancelled) setError(e.response?.data?.error || 'Could not start the exam. Please try again.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; stopSpanishAudio(); };
  }, [level]);

  // cycle the grading messages
  useEffect(() => {
    if (!grading) return undefined;
    setMsgIndex(0);
    const id = setInterval(() => setMsgIndex(i => Math.min(GRADING_MESSAGES.length - 1, i + 1)), gradingMs / GRADING_MESSAGES.length);
    return () => clearInterval(id);
  }, [grading, gradingMs]);

  // start the result transitions just after it mounts (a timer, not an animation frame, so it also runs in a
  // background tab); after a few seconds make sure every element is visible even if animations never ran
  useEffect(() => {
    if (!result) { setShown(false); setSettled(false); return undefined; }
    const a = setTimeout(() => setShown(true), 60);
    const b = setTimeout(() => setSettled(true), 4800);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, [result]);

  const q = exam && exam.questions[index];
  const last = exam && index === exam.questions.length - 1;
  const percent = useCountUp(result ? result.percent : 0, 1500, shown);

  const leave = () => {
    if (grading) return; // never walk away mid-grading
    stopSpanishAudio();
    if (exam && !result && Object.keys(answers).length > 0 && !window.confirm('Leave the exam? Your answers will be lost.')) return;
    onClose(result);
  };

  const next = async () => {
    stopSpanishAudio();
    if (!last) { setIndex(index + 1); return; }
    setError('');
    setGrading(true);
    gradingStarted.current = Date.now();
    try {
      const payload = exam.questions.map(x => ({ index: x.index, optionId: answers[x.index] || null }));
      const res = await spanish.submitExam(exam.examId, payload);
      // keep the grading scene on screen long enough to be seen, even when the server answers instantly
      await sleep(Math.max(0, gradingMs - (Date.now() - gradingStarted.current)));
      setGrading(false);
      setResult(res.data);
    } catch (e) {
      setGrading(false);
      setError(e.response?.data?.error || 'Could not submit the exam. Please try again.');
    }
  };

  const missed = result ? result.review.filter(r => !r.correct) : [];
  const passed = !!(result && result.passed);
  const total = exam ? exam.questions.length : 0;

  return (
    <div className="sp-overlay" onClick={leave}>
      {result && passed && shown && <Confetti />}
      <div className={`sp-sheet ${result ? (passed ? 'sp-sheet--pass' : 'sp-sheet--fail') : ''}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`Level ${level} exam`}>
        <div className="sp-sheet-top">
          <div className="sp-exam-title">📝 Level {level} exam</div>
          {exam && !result && !grading && <div className="sp-progress"><i style={{ width: `${(index / exam.questions.length) * 100}%` }} /></div>}
          <button className="sp-x" onClick={leave} aria-label="Close" disabled={grading}>✕</button>
        </div>

        {loading && <div className="sp-empty">Preparing your exam…</div>}
        {error && !exam && (
          <div className="sp-done">
            <div className="sp-trophy">📚</div>
            <p className="sp-sub" style={{ fontSize: 14 }}>{error}</p>
            <button className="sp-btn" onClick={() => onClose(null)}>OK</button>
          </div>
        )}
        {error && exam && <div className="sp-error">{error}</div>}

        {exam && !result && !grading && q && (
          <div key={index} className="sp-q-in">
            <div className="sp-prompt">Question {index + 1} of {exam.questions.length}</div>
            <div className="sp-big">
              {q.type === 'listen' ? (
                <>
                  <div className="sp-prompt" style={{ marginBottom: 8 }}>What does this mean?</div>
                  <button className="sp-btn" onClick={() => playSpanish({ audioUrl: q.audioUrl, es: '' })}>🔊 Listen</button>
                </>
              ) : q.type === 'es-en' ? (
                <>
                  <div className="sp-prompt" style={{ marginBottom: 6 }}>What does this mean?</div>
                  <div className="sp-es">{q.prompt}</div>
                </>
              ) : (
                <>
                  <div className="sp-prompt" style={{ marginBottom: 6 }}>How do you say…</div>
                  <div className="sp-en" style={{ fontSize: 22, fontWeight: 800 }}>{q.prompt}</div>
                </>
              )}
            </div>
            <div className="sp-options">
              {q.options.map(o => (
                <button key={o.id} className={`sp-option ${answers[q.index] === o.id ? 'sp-option--picked' : ''}`}
                  aria-pressed={answers[q.index] === o.id}
                  onClick={() => setAnswers(a => ({ ...a, [q.index]: o.id }))}>
                  {o.label}
                </button>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <button className="sp-btn" onClick={next} disabled={!answers[q.index]}>
                {last ? 'Finish exam' : 'Next'}
              </button>
            </div>
            <p className="sp-sub" style={{ textAlign: 'center', marginTop: 10 }}>Answers are checked at the end. Pass with {exam.passPercent}%.</p>
          </div>
        )}

        {/* 1) grading scene */}
        {grading && (
          <div className="sp-grading" role="status" aria-live="polite">
            <div className="sp-grading-orb">
              <i className="sp-grading-ring" />
              <span className="sp-grading-pencil">📝</span>
            </div>
            <h3 key={msgIndex} className="sp-grading-title">{GRADING_MESSAGES[msgIndex]}</h3>
            <div className="sp-grading-dots" aria-hidden="true">
              {Array.from({ length: total }, (_, i) => (
                <i key={i} style={{ animationDelay: `${(i / Math.max(1, total)) * (gradingMs - 500)}ms` }} />
              ))}
            </div>
            <p className="sp-sub">Marking {total} answers…</p>
          </div>
        )}

        {/* 2) pass / fail reveal */}
        {result && (
          <div className={`sp-done sp-result ${shown ? 'is-shown' : ''} ${settled ? 'is-settled' : ''} ${passed ? 'sp-result--pass' : 'sp-result--fail'}`}>
            <div className={`sp-trophy ${passed ? 'sp-trophy--pass' : 'sp-trophy--fail'}`}>{passed ? '🏆' : '💪'}</div>

            <div className="sp-ring-wrap">
              <svg viewBox="0 0 120 120" className="sp-ring" aria-hidden="true">
                <circle className="sp-ring-track" cx="60" cy="60" r={RING_R} />
                <circle className="sp-ring-fill" cx="60" cy="60" r={RING_R}
                  style={{ strokeDasharray: RING_LEN, strokeDashoffset: shown ? RING_LEN * (1 - result.percent / 100) : RING_LEN }} />
                {/* where the pass mark sits on the ring */}
                <line className="sp-ring-mark" x1="106" y1="60" x2="116" y2="60"
                  transform={`rotate(${(result.passPercent / 100) * 360} 60 60)`} />
              </svg>
              <div className={`sp-exam-score ${passed ? 'sp-exam-score--pass' : 'sp-exam-score--fail'}`} aria-label={`${result.percent} percent`}>{percent}%</div>
            </div>

            <h3 className="sp-result-title">{passed ? 'You passed!' : 'Not quite yet'}</h3>
            <p className="sp-sub sp-result-sub" style={{ fontSize: 14 }}>
              {result.correct} of {result.total} right · pass mark {result.passPercent}% · +{result.xp} XP
            </p>
            {result.openedLevel && <div className="sp-unlocked sp-unlocked--pop">🔓 Level {result.openedLevel} is now open!</div>}
            {result.finishedAllLevels && <div className="sp-unlocked sp-unlocked--pop">🌟 You have completed every level. ¡Increíble!</div>}
            {!passed && <p className="sp-sub sp-result-sub">Practise the words you missed, then try again - each exam asks a fresh set.</p>}

            {missed.length > 0 && (
              <div className="sp-missed">
                <div className="sp-field-label">Review these</div>
                {missed.map((m, i) => (
                  <div className="sp-row sp-row-in" style={{ animationDelay: `${1.1 + i * 0.09}s` }} key={m.itemId}>
                    <div className="sp-phrase-main">
                      <div className="sp-es" style={{ fontSize: 15 }}>{m.es}</div>
                      <div className="sp-en">{m.en}</div>
                      {m.yourAnswer && <div className="sp-ex">You answered: {m.yourAnswer}</div>}
                    </div>
                    <button className="sp-play" onClick={() => playSpanish({ audioUrl: m.audioUrl, es: m.es })} aria-label={`Listen to ${m.es}`}>🔊</button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 14, flexWrap: 'wrap' }}>
              <button className="sp-btn sp-result-cta" onClick={() => onClose(result)}>
                {result.openedLevel ? `See Level ${result.openedLevel} →` : 'Done'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
